import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
}

// API base URL for password token validation
const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL || 'https://interface-research-auth.workers.dev';

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [checkingToken, setCheckingToken] = useState(false);

  // Check for password token in URL (for simple sharing)
  const token = searchParams.get('token');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        return;
      }

      setCheckingToken(true);
      try {
        const response = await fetch(`${AUTH_API_URL}/validate-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            path: location.pathname,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setTokenValid(data.valid);
          if (data.valid && data.sessionToken) {
            // Store temporary session for this presentation
            sessionStorage.setItem(`presentation_token_${location.pathname}`, data.sessionToken);
          }
        } else {
          setTokenValid(false);
        }
      } catch (error) {
        console.error('Error validating token:', error);
        setTokenValid(false);
      } finally {
        setCheckingToken(false);
      }
    };

    // Check if we have a valid session token for this presentation
    const storedToken = sessionStorage.getItem(`presentation_token_${location.pathname}`);
    if (storedToken) {
      setTokenValid(true);
    } else if (token) {
      validateToken();
    } else {
      setTokenValid(false);
    }
  }, [token, location.pathname]);

  // Show loading state while checking auth
  if (isLoading || checkingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-glam-accent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Allow access if authenticated via OAuth or valid token
  if (isAuthenticated || tokenValid) {
    return <>{children}</>;
  }

  // Redirect to login with return URL
  return <Navigate to="/login" state={{ from: location }} replace />;
};

export default ProtectedRoute;
